/**
 * TT-VIDEO-002: Strict video URL whitelist validation (client-side).
 * Mirrors the backend rules in /app/backend/server.py (validate_playback_url / validate_thumbnail_url).
 * Returns null when the value is valid; returns a Turkish error string when invalid.
 * An empty/undefined value is treated as "not provided" (returns null — publish gate is separate).
 */

const ALLOWED_HOSTS_EXACT = new Set([
  'cdn.testettik.com',
  'video.testettik.com',
  'media.testettik.com',
]);

const ALLOWED_HOST_SUFFIX = ['.b-cdn.net'];

const BLOCKED_HOSTS = new Set([
  'youtube.com', 'www.youtube.com', 'm.youtube.com', 'music.youtube.com',
  'youtu.be', 'youtube-nocookie.com', 'www.youtube-nocookie.com',
  'i.ytimg.com', 'img.youtube.com', 'ytimg.com',
  'vimeo.com', 'www.vimeo.com', 'player.vimeo.com',
  'tiktok.com', 'www.tiktok.com', 'vm.tiktok.com', 'm.tiktok.com',
  'instagram.com', 'www.instagram.com', 'instagr.am',
  'facebook.com', 'www.facebook.com', 'm.facebook.com', 'fb.watch', 'fb.com',
  'dailymotion.com', 'www.dailymotion.com', 'dai.ly',
  'twitter.com', 'www.twitter.com', 'x.com', 'www.x.com', 't.co',
  'iframe.mediadelivery.net', 'mediadelivery.net',
  'video.bunnycdn.com', 'bunnycdn.com', 'www.bunnycdn.com',
]);

const BLOCKED_MARKERS = [
  'youtube', 'youtu.be', 'ytimg',
  'vimeo',
  'tiktok',
  'instagram', 'instagr.am',
  'facebook', 'fb.watch',
  'dailymotion', 'dai.ly',
  'iframe.mediadelivery',
  '/embed/', '/iframe/', '/player.html',
];

const BLOCKED_SCHEMES = ['javascript:', 'data:', 'blob:', 'file:', 'ftp:', 'about:', 'vbscript:'];

const ALLOWED_VIDEO_EXTS = ['.mp4', '.webm', '.mov', '.m3u8'];
const ALLOWED_THUMB_EXTS = ['.jpg', '.jpeg', '.png', '.webp'];

export const ERR_PLAYBACK_INVALID =
  'Bu video kaynağı desteklenmiyor. Lütfen Bunny/CDN üzerinden alınmış geçerli bir video oynatma bağlantısı girin.';
export const ERR_THUMBNAIL_INVALID =
  'Video kapak görseli bağlantısı geçerli değil. Lütfen Bunny/CDN üzerinden alınmış geçerli bir kapak görseli bağlantısı girin.';

const decodeSafe = (raw) => {
  try {
    return decodeURIComponent(decodeURIComponent(raw)).toLowerCase();
  } catch {
    return String(raw).toLowerCase();
  }
};

const isWhitelistedHost = (host) => {
  const h = (host || '').toLowerCase().trim();
  if (!h) return false;
  if (ALLOWED_HOSTS_EXACT.has(h)) return true;
  return ALLOWED_HOST_SUFFIX.some((s) => h === s.replace(/^\./, '') || h.endsWith(s));
};

const validateMedia = (raw, allowedExts, errorMsg) => {
  if (raw === null || raw === undefined) return null;
  const u = String(raw).trim();
  if (!u) return null;

  const low = u.toLowerCase();
  if (BLOCKED_SCHEMES.some((s) => low.startsWith(s))) return errorMsg;
  if (!low.startsWith('https://')) return errorMsg;

  // Full-URL blocked marker scan (double decoded)
  const decoded = decodeSafe(u);
  if (BLOCKED_MARKERS.some((m) => decoded.includes(m))) return errorMsg;

  let parsed;
  try {
    parsed = new URL(u);
  } catch {
    return errorMsg;
  }
  const host = (parsed.hostname || '').toLowerCase();
  const path = (parsed.pathname || '').toLowerCase();

  if (BLOCKED_HOSTS.has(host)) return errorMsg;
  if (!isWhitelistedHost(host)) return errorMsg;
  if (!path || !allowedExts.some((ext) => path.endsWith(ext))) return errorMsg;

  return null;
};

export const validatePlaybackUrl = (raw) => validateMedia(raw, ALLOWED_VIDEO_EXTS, ERR_PLAYBACK_INVALID);
export const validateThumbnailUrl = (raw) => validateMedia(raw, ALLOWED_THUMB_EXTS, ERR_THUMBNAIL_INVALID);

/**
 * Determines whether the "TestettiK Video Kanıtı" block should render publicly.
 * Combines PUBLISHED status + whitelist validation as a defense-in-depth check.
 */
export const canRenderPublicVideo = (video) => {
  if (!video || typeof video !== 'object') return false;
  if (video.status !== 'PUBLISHED') return false;
  if (!video.playbackUrl) return false;
  if (validatePlaybackUrl(video.playbackUrl)) return false;
  if (video.thumbnailUrl && validateThumbnailUrl(video.thumbnailUrl)) return false;
  return true;
};
